import { useState } from 'react';
import Modal from '../../ui/Modal/Modal';
import Input from '../../ui/Input/Input';
import Button from '../../ui/Button/Button';
import styles from './CreateChapterModal.module.css';

// Create-chapter dialog. For screenplay codices, captures the scene heading
// (INT/EXT + LOCATION + TIME OF DAY) and composes the chapter title from those
// fields. For general codices, falls back to a plain title input.
//
// onCreate(title, { description }) returns a promise. Title for screenplay = `${kind}. ${LOCATION} - ${TIME}`.
// Description is optional and captured at creation time so writers can stamp
// scene metadata before the body editor opens.

const KINDS = ['INT', 'EXT', 'INT/EXT'];
const TIME_PRESETS = ['DAY', 'NIGHT', 'MORNING', 'EVENING', 'DAWN', 'DUSK', 'CONTINUOUS', 'LATER'];

export default function CreateChapterModal({ isOpen, onClose, onCreate, codexType = 'general' }) {
  const isScreenplay = codexType === 'screenplay';
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('INT');
  const [location, setLocation] = useState('');
  const [time, setTime] = useState('DAY');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setTitle('');
    setKind('INT');
    setLocation('');
    setTime('DAY');
    setDescription('');
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const composeScreenplayTitle = () =>
    `${kind}. ${location.trim().toUpperCase()} - ${time.trim().toUpperCase()}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    let finalTitle;
    if (isScreenplay) {
      if (!location.trim()) {
        setError('Location is required');
        return;
      }
      if (!time.trim()) {
        setError('Time of day is required');
        return;
      }
      finalTitle = composeScreenplayTitle();
    } else {
      if (!title.trim()) {
        setError('Title is required');
        return;
      }
      finalTitle = title.trim();
    }
    setSubmitting(true);
    setError(null);
    try {
      await onCreate(finalTitle, { description: description.trim() });
      reset();
      onClose();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const previewTitle = isScreenplay && location.trim()
    ? composeScreenplayTitle()
    : null;

  const descriptionField = (
    <div className={styles.field}>
      <span className={styles.label}>Description (optional)</span>
      <textarea
        className={styles.descArea}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={isScreenplay ? 'Scene metadata / notes' : 'Chapter notes'}
        rows={3}
      />
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isScreenplay ? 'Create scene' : 'Create chapter'}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting}>
            Create
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        {isScreenplay ? (
          <>
            <div className={styles.field}>
              <span className={styles.label}>Scene type</span>
              <div className={styles.kindRow}>
                {KINDS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`${styles.kindBtn} ${kind === k ? styles.kindActive : ''}`}
                    onClick={() => setKind(k)}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>

            <Input
              label="Location"
              placeholder="e.g. KITCHEN, ABANDONED WAREHOUSE"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              autoFocus
            />

            <div className={styles.field}>
              <br />
              <span className={styles.label}>Time of day</span>
              <Input
                placeholder="DAY / NIGHT / CONTINUOUS"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
              <div className={styles.presetRow}>
                {TIME_PRESETS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`${styles.preset} ${time === t ? styles.presetActive : ''}`}
                    onClick={() => setTime(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {previewTitle && (
              <div className={styles.preview}>
                <span className={styles.previewLabel}>Heading:</span>
                <span className={styles.previewValue}>{previewTitle}</span>
              </div>
            )}

            {descriptionField}

            {error && <span className={styles.error}>{error}</span>}
          </>
        ) : (
          <>
            <Input
              label="Chapter title"
              placeholder="e.g. Chapter 1 — The Tavern"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={error}
              autoFocus
            />
            {descriptionField}
          </>
        )}
      </form>
    </Modal>
  );
}
